import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styles from './AdminPanel.module.css';
import Swal from 'sweetalert2';
import { getCaptcha, loginAdmin, checkAuthStatus, logoutAdmin } from '../api/auth.api';
import { fetchDomains as getDomains, updateDomains } from '../api/domains.api';
import { 
  Box, Container, Typography, Card, CardContent, TextField, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Skeleton, IconButton,
  Grid, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [password, setPassword] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [requiresCaptcha, setRequiresCaptcha] = useState(true);
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // New domain form state
  const initialFormState = {
    domain: '',
    basePrice: '',
    category: '',
    description: '',
    listedDate: new Date().toISOString().split('T')[0],
    relatedLinkTitle: '',
    relatedLinkUrl: ''
  };
  const [newDomain, setNewDomain] = useState(initialFormState);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    const verify = async () => {
      try {
        await checkAuthStatus();
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    verify();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadDomains();
    } else if (!checkingAuth) {
      loadCaptcha();
    }
  }, [isAuthenticated, checkingAuth]);

  const loadCaptcha = async () => {
    try {
      const svgText = await getCaptcha();
      setCaptchaSvg(svgText);
    } catch (err) {
      console.error('Error fetching captcha:', err);
      Swal.fire('Error de Captcha', err.message, 'error');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await loginAdmin(password, captchaText);
      setIsAuthenticated(true);
      setRequiresCaptcha(false);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.data?.error || err.message || 'Error en el login', 'error');
      setRequiresCaptcha(true);
      loadCaptcha();
    }
  };

  const loadDomains = async () => {
    setLoading(true);
    try {
      const data = await getDomains();
      setDomains(data);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Error cargando dominios: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch(err) {
      console.error('Error logging out', err);
    } finally {
      setIsAuthenticated(false);
      setPassword('');
      setCaptchaText('');
      setRequiresCaptcha(true);
      loadCaptcha();
    }
  };


  const saveDomains = async (updatedDomains) => {
    setSaving(true);
    try {
      await updateDomains(updatedDomains);
      setDomains(updatedDomains);
      Swal.fire('Éxito', 'Dominios guardados correctamente', 'success');
      setIsFormOpen(false);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Error guardando dominios', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDomain({ ...newDomain, [name]: value });
  };

  const handleEditDomain = (index) => {
    const domainToEdit = domains[index];
    setNewDomain({
      domain: domainToEdit.domain,
      basePrice: domainToEdit.basePrice || domainToEdit.price,
      category: domainToEdit.category || '',
      description: domainToEdit.description || '',
      listedDate: domainToEdit.listedDate || new Date().toISOString().split('T')[0],
      relatedLinkTitle: domainToEdit.relatedLinks?.[0]?.title || '',
      relatedLinkUrl: domainToEdit.relatedLinks?.[0]?.url || ''
    });
    setEditIndex(index);
    setIsFormOpen(true);
  };

  const handleAddDomain = (e) => {
    e.preventDefault();
    
    const domainObject = {
      domain: newDomain.domain,
      basePrice: Number(newDomain.basePrice),
      category: newDomain.category,
      description: newDomain.description,
      listedDate: newDomain.listedDate,
      contact: {
        name: process.env.REACT_APP_CONTACT_NAME || "Equipo de Ventas",
        email: process.env.REACT_APP_CONTACT_EMAIL || "dominios@rolsyssolutions.com",
        whatsapp: process.env.REACT_APP_CONTACT_WHATSAPP || "5532861000",
        phone: "--no proporcionado--"
      },
      relatedLinks: newDomain.relatedLinkUrl ? [
        {
          title: newDomain.relatedLinkTitle || 'Link',
          description: '',
          url: newDomain.relatedLinkUrl
        }
      ] : [],
      price: newDomain.basePrice
    };

    let updatedDomains;
    if (editIndex >= 0) {
      updatedDomains = [...domains];
      updatedDomains[editIndex] = domainObject;
    } else {
      updatedDomains = [...domains, domainObject];
    }
    
    saveDomains(updatedDomains);
  };

  const handleDeleteDomain = async (index) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'No podrás revertir esta acción',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      const updated = domains.filter((_, i) => i !== index);
      saveDomains(updated);
    }
  };

  if (checkingAuth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'radial-gradient(circle at top right, #111827 0%, #0f172a 100%)' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box className={styles.adminContainer}>
        <Helmet>
          <title>Login - Panel de Administración</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Card sx={{ 
          maxWidth: 400, 
          width: '100%', 
          p: 2, 
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#F8FAFC', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
              Acceso Restringido
            </Typography>
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                sx={{ mb: 2, input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
              />
              
              {requiresCaptcha && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box 
                      dangerouslySetInnerHTML={{ __html: captchaSvg }} 
                      sx={{ background: 'white', borderRadius: 1, display: 'flex', justifyContent: 'center', p: 1, flexGrow: 1 }}
                    />
                    <IconButton onClick={loadCaptcha} sx={{ color: 'white', ml: 1 }}>
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    type="text"
                    placeholder="Ingrese el captcha"
                    value={captchaText}
                    onChange={(e) => setCaptchaText(e.target.value)}
                    variant="outlined"
                    sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                  />
                </Box>
              )}
              
              <Button type="submit" variant="contained" fullWidth sx={{ py: 1.5, mt: 1, borderRadius: 2 }}>
                Ingresar
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={styles.adminContainer}>
      <Helmet>
        <title>Panel de Administración | Dominios</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ color: '#F8FAFC', fontWeight: 'bold' }}>
            Panel de Administración
          </Typography>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ borderRadius: 2 }}
          >
            Cerrar Sesión
          </Button>
        </Box>
        
        <Card sx={{ 
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 4,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          mb: 4
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#60A5FA' }}>Gestión de Dominios</Typography>
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewDomain(initialFormState);
                  setEditIndex(-1);
                  setIsFormOpen(true);
                }}
                sx={{ borderRadius: 2 }}
              >
                Nuevo Dominio
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.05)' }} />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Dominio</TableCell>
                      <TableCell sx={{ color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Categoría</TableCell>
                      <TableCell sx={{ color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Precio</TableCell>
                      <TableCell align="right" sx={{ color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.map((domain, index) => (
                      <TableRow key={domain.domain} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ color: '#F8FAFC', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{domain.domain}</TableCell>
                        <TableCell sx={{ color: '#F8FAFC', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{domain.category}</TableCell>
                        <TableCell sx={{ color: '#F8FAFC', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>${domain.basePrice || domain.price}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <IconButton size="small" color="warning" onClick={() => handleEditDomain(index)} sx={{ mr: 1 }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteDomain(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {domains.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#94A3B8', py: 3, border: 0 }}>
                          No hay dominios registrados
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      <Dialog 
        open={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1E293B',
            color: '#F8FAFC',
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {editIndex >= 0 ? 'Editar Dominio' : 'Agregar Nuevo Dominio'}
        </DialogTitle>
        <form onSubmit={handleAddDomain}>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Nombre del Dominio" name="domain" value={newDomain.domain} onChange={handleInputChange} disabled={editIndex >= 0} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Precio Base ($)" name="basePrice" type="number" value={newDomain.basePrice} onChange={handleInputChange} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Categoría" name="category" value={newDomain.category} onChange={handleInputChange} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth label="Fecha de Listado" name="listedDate" type="date" value={newDomain.listedDate} onChange={handleInputChange} InputLabelProps={{ shrink: true, style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField required fullWidth label="Descripción" name="description" value={newDomain.description} onChange={handleInputChange} multiline rows={3} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ textarea: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Enlace Relacionado: Título" name="relatedLinkTitle" value={newDomain.relatedLinkTitle} onChange={handleInputChange} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Enlace Relacionado: URL" name="relatedLinkUrl" type="url" value={newDomain.relatedLinkUrl} onChange={handleInputChange} InputLabelProps={{ style: { color: '#94A3B8' } }} sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Button onClick={() => setIsFormOpen(false)} sx={{ color: '#94A3B8' }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Guardando...' : (editIndex >= 0 ? 'Guardar Cambios' : 'Añadir Dominio')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;
